/**
 * Procedural placeholder fuel truck (aviation refueler).
 *
 * A throwaway low-poly stand-in until real GLB refueler models exist. Built in
 * METERS (like the procedural tug) with the FRONT facing +Z, wheels resting on
 * y = 0, and origin centered on the footprint — so the same heading math and
 * `updateModelTransform` used for aircraft/tugs apply unchanged.
 *
 * The horizontal tank is tinted by fuel type so Jet-A vs Avgas (100LL) reads at
 * a glance: Jet-A trucks are dark/grey, Avgas trucks are 100LL blue.
 *
 * Swap-in path: once a GLB exists, render it through the normal aircraft loader
 * with `modelSettings` instead of this factory.
 */
import * as THREE from 'three'
import type { FuelType } from '../motion/fuelRequest'

// Per-instance materials. NOT shared singletons: the layer service disposes an
// object's materials on removal, so sharing would destroy other live trucks.
const makeCabMat = () => new THREE.MeshStandardMaterial({ color: 0xe8e8ea, roughness: 0.5, metalness: 0.1 })
const makeDarkMat = () => new THREE.MeshStandardMaterial({ color: 0x2b2b2b, roughness: 0.7, metalness: 0.2 })
const makeTankMat = (fuelType: FuelType) =>
  new THREE.MeshStandardMaterial({
    // Avgas/100LL → aviation blue; Jet-A → dark steel.
    color: fuelType === 'avgas' ? 0x2b6cb0 : 0x3a3f45,
    roughness: 0.45,
    metalness: 0.35,
  })
const makeBeaconMat = () =>
  new THREE.MeshStandardMaterial({ color: 0xffb020, emissive: 0xff7a00, emissiveIntensity: 0.6, roughness: 0.4 })

// Dimensions in meters (a compact apron refueler — longer than the tug).
const BODY_L = 6.2
const BODY_W = 2.2
const CHASSIS_H = 0.5
const CAB_L = 1.7
const CAB_H = 1.4
const WHEEL_R = 0.42
const WHEEL_W = 0.32
const TANK_R = 0.95

export class ProceduralFuelTruckFactory {
  /** Build a fuel-truck group. Front (cab) = +Z, wheels at y = 0, centered on X/Z. */
  static create(fuelType: FuelType = 'jet_a'): THREE.Group {
    const g = new THREE.Group()
    const cabMat = makeCabMat()
    const darkMat = makeDarkMat()
    const tankMat = makeTankMat(fuelType)
    const beaconMat = makeBeaconMat()

    const chassisY = WHEEL_R + CHASSIS_H / 2
    const chassis = new THREE.Mesh(new THREE.BoxGeometry(BODY_W, CHASSIS_H, BODY_L), darkMat)
    chassis.position.set(0, chassisY, 0)
    g.add(chassis)

    // Cab at the front (+Z).
    const cab = new THREE.Mesh(new THREE.BoxGeometry(BODY_W * 0.95, CAB_H, CAB_L), cabMat)
    cab.position.set(0, WHEEL_R + CHASSIS_H + CAB_H / 2, BODY_L / 2 - CAB_L / 2)
    g.add(cab)

    // Horizontal cylindrical tank over the rear bed (axis along Z = travel).
    const tankLen = BODY_L - CAB_L - 0.6
    const tank = new THREE.Mesh(new THREE.CylinderGeometry(TANK_R, TANK_R, tankLen, 16), tankMat)
    tank.rotation.x = Math.PI / 2 // lay the cylinder along Z
    tank.position.set(0, WHEEL_R + CHASSIS_H + TANK_R, -CAB_L / 2 - 0.3)
    g.add(tank)

    // Rotating beacon on the cab roof.
    const beacon = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.11, 0.18, 8), beaconMat)
    beacon.position.set(0, WHEEL_R + CHASSIS_H + CAB_H + 0.09, BODY_L / 2 - CAB_L / 2)
    g.add(beacon)

    // Six wheels (two front, four rear) laid on the X axis.
    const wheelGeo = new THREE.CylinderGeometry(WHEEL_R, WHEEL_R, WHEEL_W, 12)
    const halfW = BODY_W / 2
    for (const sx of [-1, 1]) {
      for (const sz of [BODY_L * 0.36, -BODY_L * 0.12, -BODY_L * 0.34]) {
        const w = new THREE.Mesh(wheelGeo, darkMat)
        w.rotation.z = Math.PI / 2
        w.position.set(sx * halfW, WHEEL_R, sz)
        g.add(w)
      }
    }

    g.traverse((c) => {
      if (c instanceof THREE.Mesh) {
        c.castShadow = true
        c.receiveShadow = true
      }
    })

    // Transform base so updateModelTransform() places it on the ground (wheels
    // already model y = 0) and applies heading on Y.
    g.userData.transformBase = {
      kind: 'procedural',
      yOffsetFeet: 0,
      baseRot: [0, 0, 0] as [number, number, number],
      headingAxis: 'y' as const,
      proceduralGroundY: 0,
    }

    return g
  }
}
